import { useCallback, useMemo } from 'react';
import type { UpdateCatalogItemInput } from '@peles/shared';
import { useSettingsCatalogItems, useUpdateSettingsCatalogItem } from '../../hooks/useSettings';
import { useVendorCategories } from '../../hooks/useVendors';
import { QuickEditModal, type QuickEditField } from '../common/QuickEditModal';

const FIELDS: QuickEditField[] = [
  { key: 'category', label: 'Category', readOnly: true },
  { key: 'name', label: 'Name', required: true },
  { key: 'nameHe', label: 'Name (Hebrew)' },
];

export interface CatalogItemQuickEditModalProps {
  catalogItemId: string | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Quick view/edit of a catalog item's key details, opened whenever an item is
 * linked somewhere (expense line, planning/itemized row, ...).
 */
export function CatalogItemQuickEditModal({
  catalogItemId,
  open,
  onClose,
}: CatalogItemQuickEditModalProps) {
  const { data: catalogItems, isLoading } = useSettingsCatalogItems();
  const { data: categories = [] } = useVendorCategories();
  const updateCatalogItem = useUpdateSettingsCatalogItem();

  const item = useMemo(
    () => catalogItems?.find((c) => c.id === catalogItemId) ?? null,
    [catalogItems, catalogItemId],
  );

  const initialValues = useMemo(
    () => ({
      category: categories.find((c) => c.id === item?.vendorCategoryId)?.name ?? '',
      name: item?.name ?? '',
      nameHe: item?.nameHe ?? '',
    }),
    [item, categories],
  );

  const handleSave = useCallback(
    async (values: Record<string, string>) => {
      if (!catalogItemId) return;
      const updates: UpdateCatalogItemInput = {};
      if (values.name.trim() !== initialValues.name) updates.name = values.name.trim();
      if ((values.nameHe ?? '') !== initialValues.nameHe) {
        updates.nameHe = values.nameHe.trim() || null;
      }
      if (Object.keys(updates).length === 0) return;
      await updateCatalogItem.mutateAsync({ id: catalogItemId, updates });
    },
    [catalogItemId, initialValues, updateCatalogItem],
  );

  return (
    <QuickEditModal
      open={open && !!catalogItemId}
      onClose={onClose}
      title="Catalog Item"
      fields={FIELDS}
      initialValues={initialValues}
      onSave={handleSave}
      saving={updateCatalogItem.isPending}
      loading={isLoading}
    />
  );
}
